/**
 * Themes become CSS custom properties (docs/design/theming.md): one rule per theme
 * under `[data-theme="<id>"]`, plus `[data-theme="<id>"][data-scheme="<scheme>"]` for an
 * alternate look. The provider sets those attributes on <html>; any element can also
 * carry `data-theme` to render a live preview of another theme (the theme picker does).
 */
import type { Theme, ThemeColors, ThemeLook } from '../themes/types.js';
import {
  CATEGORY_COUNT,
  COLOR_TOKENS,
  FONT_SIZES,
  RADII,
  SHADOWS,
  TABLE_STATES,
} from '../themes/types.js';

/** `onAccentSoft` → `on-accent-soft`. */
export function kebab(name: string): string {
  return name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

/** The CSS variable name of a colour token: `--color-on-accent`. */
export function colorVar(token: string): string {
  return `--color-${kebab(token)}`;
}

function colorDeclarations(colors: ThemeColors): [string, string][] {
  const out: [string, string][] = COLOR_TOKENS.map((t) => [colorVar(t), colors[t]]);
  for (const state of TABLE_STATES) {
    out.push([`--color-table-${state}`, colors.table[state].bg]);
    out.push([`--color-on-table-${state}`, colors.table[state].fg]);
  }
  for (let i = 0; i < CATEGORY_COUNT; i++) {
    const c = colors.categories[i];
    if (!c) continue;
    out.push([`--color-category-${i + 1}`, c.bg]);
    out.push([`--color-on-category-${i + 1}`, c.fg]);
  }
  return out;
}

function lookDeclarations(look: ThemeLook): [string, string][] {
  return [
    ['color-scheme', look.colorScheme],
    ...colorDeclarations(look.colors),
    ...SHADOWS.map((s): [string, string] => [`--shadow-${s}`, look.shadows[s]]),
  ];
}

/** Every CSS variable a theme defines, by name (the primary look). */
export function themeVariables(theme: Theme): Map<string, string> {
  return new Map([
    ...lookDeclarations(theme.look),
    ['--font-sans', theme.typography.fontSans],
    ['--font-numeric', theme.typography.fontNumeric],
    ...FONT_SIZES.map((s): [string, string] => [`--font-size-${s}`, theme.typography.sizes[s]]),
    ['--line-height', String(theme.typography.lineHeight)],
    ['--font-weight-regular', String(theme.typography.weights.regular)],
    ['--font-weight-medium', String(theme.typography.weights.medium)],
    ['--font-weight-bold', String(theme.typography.weights.bold)],
    ...RADII.map((r): [string, string] => [`--radius-${r}`, theme.radii[r]]),
    ['--space-unit', `${theme.space}px`],
    ['--touch-min', `${theme.touchMin}px`],
  ]);
}

const block = (selector: string, decls: Iterable<[string, string]>) =>
  `${selector}{${[...decls].map(([k, v]) => `${k}:${v}`).join(';')}}`;

/** The stylesheet for a theme: its look, and its alternate look when it has one. */
export function themeToCss(theme: Theme): string {
  const sel = `[data-theme="${theme.id}"]`;
  const rules = [block(sel, themeVariables(theme))];
  // The primary look again under its own scheme, so a nested preview inside an element
  // that selected the alternate scheme still shows the primary look when asked to.
  rules.push(
    block(`${sel}[data-scheme="${theme.look.colorScheme}"]`, lookDeclarations(theme.look)),
  );
  if (theme.alternate) {
    rules.push(
      block(
        `${sel}[data-scheme="${theme.alternate.colorScheme}"]`,
        lookDeclarations(theme.alternate),
      ),
    );
  }
  return rules.join('\n');
}

export const THEME_STYLE_ELEMENT_ID = 'qrkey-themes';

/** Adds (or refreshes) one <style> element with every registered theme. Idempotent. */
export function installThemeStyles(themes: readonly Theme[], doc: Document = document): void {
  const css = themes.map(themeToCss).join('\n');
  let el = doc.getElementById(THEME_STYLE_ELEMENT_ID);
  if (!el) {
    el = doc.createElement('style');
    el.id = THEME_STYLE_ELEMENT_ID;
    doc.head.appendChild(el);
  }
  if (el.textContent !== css) el.textContent = css;
}
